import React from "react";

import Box from "../Box";

import styled from "styled-components";
import { CText } from "@carrefour/design-system-components-react";
import BaseDashboardCard from "../BaseDashboardCard";
import { Rupture } from "../../types/operational";
import useBreakPoint from "../../utils/useBreakPoint";
import { useTranslation } from "react-i18next";
import { formatPercentage } from "../../utils/numberUtils";

const Text = styled(CText)`
  font-family: system-ui;
`;
const Table = styled.table`
  width: 100%;
  align-items: center;
  border-collapse: collapse;
`;
const Tr = styled.tr``;
const TrCustom = styled.tr<{ isLast: boolean }>`
  border-collapse: collapse;
  border-bottom: ${(props) => (props?.isLast ? "" : " 1px solid #f0f3f6")};
`;
const Th = styled.th<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;
const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
`;

type BreaksNotedCardProps = {
  title: string;
  listValue: Rupture["stockoutsDetailByDepartment"];
  isUnavailable: boolean;
  aval: number;
  amont: number;
  messageError?: string;
  ruptureTotal: number;
  isLoading: boolean;
  importedDateTime?: string;
  openInformationModal: (title?: string) => void;
};

const BreaksNotedCard = ({
  title,
  listValue,
  aval,
  amont,
  ruptureTotal,
  importedDateTime,
  ...res
}: BreaksNotedCardProps) => {
  const isBreak = useBreakPoint();
  const {t} =useTranslation();
  return (
    <BaseDashboardCard hasModal={true} title={title} size={"big"} {...res} importedDateTime={importedDateTime}>
      <BoxCustom
        gap="1.5rem"
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
      >
        <BoxCustom
          gap={`0.8rem`}
          flexDirection={"column"}
          alignItems={"center"}
          width="100%"
          borderRadius={1}
          pt={[1, 4]}
        >
          {/* Rupture Total */}
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            width={"100%"}
          >
            <Box display={"flex"} alignItems={"baseline"} pb={1}>
              <Text size={"m"} tagStyle="h1">{`${new Intl.NumberFormat(
                "fr-FR",
                {
                  maximumFractionDigits: 0,
                }
              ).format(ruptureTotal)}`}</Text>
            </Box>
          </Box>
          <Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
            {/* amont */}
            <Box
              borderRadius={1}
              border={"1px solid #BDC7DA"}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              py={1}
              px={2}
              width={"47%"}
            >
              <Box display={"flex"} alignItems={"center"} pt={1}>
                <Text size={"s"} tagStyle="p">
                  {t('operationnel.causes_amont')}
                </Text>
              </Box>
              <Box display={"flex"} alignItems={"baseline"} py={"4px"}>
                <Text size={"m"} tagStyle="h1">{`${formatPercentage(amont, '')}`}</Text>
                <Text tagStyle="p">{`%`}</Text>
              </Box>
            </Box>
            {/* aval */}
            <Box
              borderRadius={1}
              border={"1px solid #BDC7DA"}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"flex-start"}
              py={1}
              px={2}
              width={"47%"}
            >
              <Box display={"flex"} alignItems={"center"} pt={1}>
                <Text size={"s"} tagStyle="p">
                  {t('operationnel.causes_aval')}
                </Text>
              </Box>
              <Box display={"flex"} alignItems={"baseline"} py={"4px"}>
                <Text size={"m"} tagStyle="h1">{`${formatPercentage(aval, '')}`}</Text>
                <Text tagStyle="p">{`%`}</Text>
              </Box>
            </Box>
          </Box>
        </BoxCustom>
        {/* famille */}
        <Box alignItems="center" justifyContent={"center"} width="100%">
          <Table>
            <thead>
              <Tr>
                <Th $textAlign={"left"}>
                  <Text bold size={"s"} tagStyle={"subtitle"}>
                    {t('operationnel.famille')}
                  </Text>
                </Th>
                <Th></Th>
              </Tr>
            </thead>
            <tbody>
              {listValue &&
                listValue?.length > 0 &&
                listValue?.map((breakNoted, index) =>
                  subElements(
                    breakNoted,
                    isBreak,
                    listValue?.length === index + 1
                  )
                )}
            </tbody>
          </Table>
        </Box>
      </BoxCustom>
    </BaseDashboardCard>
  );
};

export default BreaksNotedCard;

function subElements(
  breakNoted: {
    departmentKey: string;
    departmentDesc: string;
    stockoutsCount: number;
  },
  isBreak: string,
  isLast: boolean
) {
  return (
    <TrCustom isLast={isLast} key={"breaksNoted" + breakNoted.departmentKey}>
      <Td $textAlign={"left"}>
        <BoxCustom gap="2px" display={"flex"}>
          {breakNoted?.departmentKey !== "XX" ? (
            <>
              <Text size={"m"} tagStyle="p">
                {breakNoted.departmentKey}
              </Text>
              <Text size={"m"} tagStyle="p">
                {":"}
              </Text>
            </>
          ) : null}
          <Text size={"m"} tagStyle="p">
            {breakNoted.departmentDesc}
          </Text>
        </BoxCustom>
      </Td>
      <Td $textAlign={"right"}>
        <Text bold>
          {!isNaN(breakNoted.stockoutsCount)
            ? `${new Intl.NumberFormat("fr-FR", {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              }).format(breakNoted.stockoutsCount)}`
            : "-"}
        </Text>
      </Td>
    </TrCustom>
  );
}
